import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { PageHeader, Loading, ErrorState, EmptyState } from "@/components/common";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ChevronRight, Dumbbell, Shield, MessageSquare } from "lucide-react";
import { toast } from "sonner";

const asList = (d) => (Array.isArray(d) ? d : d?.items || d?.exercises || d?.results || d?.days || []);

export default function PatientDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [program, setProgram] = useState(null);
  const [stats, setStats] = useState(null);
  const [exMap, setExMap] = useState({});
  const [braceHours, setBraceHours] = useState("");
  const [braceToday, setBraceToday] = useState(null);
  const [savingBrace, setSavingBrace] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const today = new Date().toISOString().slice(0, 10);

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const [p, s, ex, b] = await Promise.allSettled([
        api.patientProgram(user.id),
        api.patientStats(user.id),
        api.exercises(),
        api.braceLogs(user.id),
      ]);
      if (p.status === "fulfilled") setProgram(p.value);
      if (s.status === "fulfilled") setStats(s.value);
      if (ex.status === "fulfilled") {
        const arr = Array.isArray(ex.value) ? ex.value : ex.value?.items || [];
        setExMap(Object.fromEntries(arr.map((e) => [e.id, e])));
      }
      if (b.status === "fulfilled") {
        const logs = b.value?.logs || [];
        const t = logs.find((l) => l.date === today);
        setBraceToday(t ? t.hours : null);
        if (t) setBraceHours(String(t.hours));
      }
      if (p.status === "rejected" && s.status === "rejected") setError(errMsg(p.reason));
    } catch (e) { setError(errMsg(e)); }
    finally { setLoading(false); }
  }, [user.id, today]);

  useEffect(() => { load(); }, [load]);

  const saveBrace = async () => {
    const h = parseFloat(braceHours);
    if (isNaN(h) || h < 0 || h > 24) { toast.error("Inserisci ore valide (0-24)"); return; }
    setSavingBrace(true);
    try {
      await api.createBraceLog({ patient_id: user.id, hours: h, date: today });
      setBraceToday(h);
      toast.success("Utilizzo busto registrato");
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSavingBrace(false); }
  };

  if (loading) return <Loading />;
  if (error) return <ErrorState message={error} onRetry={load} />;

  const adherencePct = typeof stats?.adherence_pct === "number" ? Math.round(stats.adherence_pct) : null;
  const sessions7 = stats?.sessions_last_7 ?? "—";
  const todayExercises = asList(program?.today || program?.exercises || program);

  const weekDays = Array.from({ length: 7 }).map((_, i) => {
    const monday = new Date();
    const dow = (monday.getDay() + 6) % 7;
    monday.setDate(monday.getDate() - dow + i);
    return monday;
  });
  const dayLetters = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];

  return (
    <div>
      <PageHeader title={`Ciao, ${user?.full_name?.split(" ")[0] || user?.username}`} subtitle="Il tuo percorso riabilitativo, ogni giorno." />

      <div className="mb-6 grid gap-4 sm:grid-cols-2">
        <div className="rounded-2xl bg-[#e6f0e9] p-5" data-testid="stat-sessions">
          <p className="text-3xl font-bold text-[#1f3a5f]">{sessions7}</p>
          <p className="text-sm text-[#1f3a5f]/70">Sessioni (7gg)</p>
        </div>
        <div className="rounded-2xl bg-[#fbe9db] p-5" data-testid="stat-adherence">
          <p className="text-3xl font-bold text-[#1f3a5f]">{adherencePct != null ? `${adherencePct}%` : "—"}</p>
          <p className="text-sm text-[#1f3a5f]/70">Aderenza</p>
        </div>
      </div>

      <Card className="mb-6 border-border/70">
        <CardContent className="flex items-center gap-4 p-5">
          <div className="grid h-11 w-11 place-items-center rounded-2xl bg-primary/10"><MessageSquare className="h-5 w-5 text-primary" /></div>
          <div className="flex-1">
            <p className="font-semibold text-[#1f3a5f]">Contatta il tuo fisioterapista</p>
            <p className="text-sm text-muted-foreground">Scrivi in chat per dubbi sul programma.</p>
          </div>
          <Button variant="outline" onClick={() => navigate("/patient/chat")} data-testid="contact-chat-button">
            <MessageSquare className="mr-2 h-4 w-4" /> Chat
          </Button>
        </CardContent>
      </Card>

      <div className="mb-6 grid grid-cols-7 gap-2" data-testid="week-strip">
        {weekDays.map((d, i) => {
          const isToday = d.toISOString().slice(0, 10) === today;
          return (
            <div key={i} className={`flex flex-col items-center rounded-xl border py-2 ${isToday ? "border-primary bg-primary text-primary-foreground" : "border-border bg-white text-[#1f3a5f]"}`}>
              <span className="text-xs opacity-70">{dayLetters[i]}</span>
              <span className="text-lg font-bold">{d.getDate()}</span>
            </div>
          );
        })}
      </div>

      <Card className="mb-6 border-none bg-[#fbe9db]">
        <CardContent className="p-5">
          <div className="mb-3 flex items-center gap-3">
            <div className="grid h-10 w-10 place-items-center rounded-2xl bg-white/70"><Shield className="h-5 w-5 text-[#5f9c96]" /></div>
            <div>
              <p className="font-bold text-[#1f3a5f]">Utilizzo del busto</p>
              <p className="text-sm text-[#1f3a5f]/70">Ore indossate oggi</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Input type="number" min="0" max="24" step="0.1" value={braceHours} onChange={(e) => setBraceHours(e.target.value)} placeholder="0" className="max-w-[110px] bg-white" data-testid="brace-hours-input" />
            <span className="text-sm text-[#1f3a5f]/70">ore / 24</span>
            <Button className="ml-auto" onClick={saveBrace} disabled={savingBrace} data-testid="save-brace-button">{savingBrace ? "..." : "Salva"}</Button>
          </div>
          {braceToday != null && <p className="mt-2 text-sm font-medium text-[#5f9c96]">Registrato: {braceToday} ore</p>}
        </CardContent>
      </Card>

      <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Esercizi di oggi</p>
      <Card className="border-border/70">
        <CardContent className="pt-6">
          {todayExercises.length === 0 ? (
            <EmptyState title="Nessun esercizio programmato" subtitle="Il tuo fisioterapista non ha ancora assegnato esercizi per oggi." />
          ) : (
            <div className="space-y-3" data-testid="today-exercises">
              {todayExercises.map((ex, i) => {
                const id = ex.exercise_id || ex.id || ex._id;
                const name = exMap[id]?.title || ex.name || ex.title || ex.exercise_name || `Esercizio ${i + 1}`;
                return (
                  <button
                    key={id || i}
                    onClick={() => id && navigate(`/patient/exercise/${id}`)}
                    className="flex w-full items-center gap-4 rounded-2xl border border-border/70 bg-white p-4 text-left transition-all hover:border-primary/40 hover:shadow-sm"
                    data-testid={`exercise-item-${i}`}
                  >
                    <div className="grid h-11 w-11 place-items-center rounded-xl bg-primary/10 text-primary">
                      <Dumbbell className="h-5 w-5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-semibold">{name}</p>
                      <div className="mt-1 flex flex-wrap gap-2 text-xs text-muted-foreground">
                        {ex.sets && <Badge variant="secondary">{ex.sets} serie</Badge>}
                        {ex.reps && <Badge variant="secondary">{ex.reps} rip.</Badge>}
                        {(ex.duration || ex.duration_seconds) && <Badge variant="secondary">{ex.duration || ex.duration_seconds}s</Badge>}
                      </div>
                    </div>
                    <ChevronRight className="h-5 w-5 text-muted-foreground" />
                  </button>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
